import { Component, OnDestroy, ViewChild, ElementRef, AfterViewInit } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { GoldenLayoutComponent } from "../../golden-layout/golden-layout-component";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { GoldenLayoutPopout } from "app/golden-layout/golden-layout-popout";
import { InterventionDataLookupService } from "shared/cubiq-services";
import { ManualPricingFixingTabModel, ManualPricingRateModel } from "./models/manual-pricing.model";
import { ManualPricingManagerService } from "./services/manual-pricing-manager.service";
import { Subscription } from "rxjs";
import {
  ManualPricingCmdPayload,
  ManualPricingCmd,
} from "./models/manual-pricing-cmd-payload.model";

@Component({
  selector: "manual-pricing-container",
  templateUrl: "./html/manual-pricing-container.html",
  standalone: false,
})
export class ManualPricingContainerComponent
  implements GoldenLayoutComponent, OnDestroy, AfterViewInit
{
  /***************************************/
  /* DATA */

  public parentPopout: GoldenLayoutPopout;
  public viewInitComplete: boolean = false;
  //
  public fixingTabDataset: Array<ManualPricingFixingTabModel> =
    new Array<ManualPricingFixingTabModel>();
  public activeFixingTabData: ManualPricingFixingTabModel = null;
  //
  public disablePrice: boolean = false;
  //
  public tabIndex: number = 0;
  public ascending: boolean = false;
  public sortingTitle: string = "Descending";
  //
  public scrollActive: boolean = false;
  public emptyRowHeight: string = "0px";
  public headerRowRightPadding: string = "0px";
  public scrollbarWidth: number = 10;
  public contentRowsHeight: string = "0px";

  //@ViewChild('contentRowsContainer', {static: true}) protected contentRowsContainer: ElementRef;
  @ViewChild("contentBodyContainer", { static: true })
  protected contentBodyContainer: ElementRef;

  public labels: any = {
    buttons: {
      cancel_key: "dealer.intervention.manual.price.label.btn_cancel",
      cancel: "Cancel",
      price_key: "dealer.intervention.manual.price.label.btn_price",
      price: "Price",
    },
    fixing_rate_key: "dealer.intervention.manual.price.label.fixing_rate",
    fixing_rate: "Fixing rate",
    instrument_key: "dealer.intervention.manual.price.label.instrument",
    instrument: "Instrument",
    bid_key: "dealer.intervention.manual.price.label.bid",
    bid: "Bid",
    mid_key: "dealer.intervention.manual.price.label.mid",
    mid: "Mid",
    offer_key: "dealer.intervention.manual.price.label.offer",
    offer: "Offer",
  };

  // subscriptions
  private managerSubscription: Subscription;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(
    public translate: TranslateService,
    public interventionDataService: InterventionDataLookupService,
    private manualPricingManager: ManualPricingManagerService,
  ) {
    this.initialize();
  }

  protected initialize(): void {
    this.subscribeToManager();
    this.initializeLabelTranslations();
  }

  ngOnDestroy() {
    this.manualPricingManager.interfaceOffline();
    this.manualPricingManager.closeAll();
    if (this.managerSubscription) {
      this.managerSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    this.viewInitComplete = true;
    this.defineScrollbarWidth();
    this.adjustContentHeight();
    this.updatePriceButtonState();
    this.applyFirstLastOnRow();
  }

  glInit(data: any, container: any, state: any): void {
    this.updateDatasetAndSetDefaultActiveTab(data.payload);
    this.updatePriceButtonState();
  }

  protected initializeLabelTranslations(): void {
    this.labels.fixing_rate = this.translate.instant(this.labels.fixing_rate_key);
    this.labels.instrument = this.translate.instant(this.labels.instrument_key);
    this.labels.bid = this.translate.instant(this.labels.bid_key);
    this.labels.mid = this.translate.instant(this.labels.mid_key);
    this.labels.offer = this.translate.instant(this.labels.offer_key);

    this.labels.buttons.cancel = this.translate.instant(this.labels.buttons.cancel_key);
    this.labels.buttons.price = this.translate.instant(this.labels.buttons.price_key);
  }

  protected defineScrollbarWidth(): void {
    let scrollDiv = document.createElement("div");
    scrollDiv.style.overflow = "scroll";
    document.body.appendChild(scrollDiv);
    // Get the scrollbar width
    this.scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    //logger.out.warn(`Scrollbar width => `, this.scrollbarWidth); // Mac:  15
    if (this.scrollbarWidth == 0) {
      this.scrollbarWidth = 10;
    }
    // Delete the DIV
    document.body.removeChild(scrollDiv);
  }

  /**********************************************************************/
  /* MANAGER SUBSCRIPTION */

  private subscribeToManager(): void {
    this.managerSubscription = this.manualPricingManager.interfaceCmd$.subscribe(
      ($event: ManualPricingCmdPayload) => {
        logger.out.info(
          `Manual-Price-Container: CMD = ${ManualPricingCmd[$event.cmd]} | payload = `,
          $event.payload,
        );
        switch ($event.cmd) {
          case ManualPricingCmd.START:
            this.manualPricingManager.interfaceOnline();
            break;
          case ManualPricingCmd.ACTIVATE:
            this.setActiveFixingTab($event.payload);
            break;
          case ManualPricingCmd.EXIT:
            this.closeWindow();
            break;
          case ManualPricingCmd.ADD:
          case ManualPricingCmd.UPDATE_PRICE_BTN:
          case ManualPricingCmd.UPDATE_WARNING:
          case ManualPricingCmd.UPDATE:
            this.adjustContentHeight();
            this.updatePriceButtonState();
            break;
          default:
            logger.out.error(
              `ERROR: Manual-Price-Container: subscribeToManager(): Command not implemented! command= (${
                $event.cmd
              }) [${$event.cmd != null ? ManualPricingCmd[$event.cmd] : "NULL"}]`,
            );
            break;
        }
      },
    );
  }

  /**********************************************************************/
  /* INTERNAL */
  private setActiveFixingTab(id: string): void {
    if (
      typeof id === "string" &&
      Array.isArray(this.fixingTabDataset) &&
      this.fixingTabDataset.length > 0
    ) {
      if (this.activeFixingTabData == null || id != this.activeFixingTabData.id) {
        let toActivate = this.fixingTabDataset.find((item) => item.id === id);
        if (toActivate instanceof ManualPricingFixingTabModel) {
          this.activeFixingTabData = toActivate;
          this.adjustContentHeight();
          this.updatePriceButtonState();
        } else {
          logger.out.error(
            `ERROR: Manual-Price-Container: setActiveFixingTab(): data not found for Id ${id}`,
          );
        }
      }
    } else {
      logger.out.error(
        `ERROR: Manual-Price-Container: setActiveFixingTab(): Invalid Id or dataset`,
      );
    }

    this.applyFirstLastOnRow();
  }

  private updateDatasetAndSetDefaultActiveTab(
    fixingTabDataset: Array<ManualPricingFixingTabModel>,
  ): void {
    this.fixingTabDataset = fixingTabDataset;
    if (
      Array.isArray(fixingTabDataset) &&
      fixingTabDataset.length > 0 &&
      fixingTabDataset[0] instanceof ManualPricingFixingTabModel
    ) {
      this.activeFixingTabData = fixingTabDataset[0];
      this.adjustContentHeight();
    }
  }

  private adjustContentHeight(): void {
    setTimeout(() => {
      if (this.activeFixingTabData != null) {
        let headerRowHeight: number = 29;
        let totalContentRowHeight: number = this.activeFixingTabData.rateData.length * 34;
        let bodyHeight: number = this.contentBodyContainer.nativeElement.offsetHeight;
        let warningRowHeight: number = this.activeFixingTabData.warning != null ? 25 : 0;
        let contentHeight: number = bodyHeight - (headerRowHeight + warningRowHeight);
        // calculations
        this.contentRowsHeight = contentHeight + "px";
        if (totalContentRowHeight > contentHeight) {
          this.scrollActive = true;
          this.emptyRowHeight = "0px";
          this.headerRowRightPadding = this.scrollbarWidth + "px";
        } else {
          this.scrollActive = false;
          this.emptyRowHeight = contentHeight - totalContentRowHeight + "px";
          this.headerRowRightPadding =
            contentHeight - 3 <= totalContentRowHeight ? this.scrollbarWidth + "px" : "0px";
        }

        //logger.out.warn(`Manual-Price: Container: adjustHeigh CALC:  scroll= ${this.scrollActive? 'YES': 'NO'} | emptyRowHeight =  ${this.emptyRowHeight} | totalHeight = ${totalHeight} | contentHeight = ${contentHeight} | this.headerRowRightPadding = ${this.headerRowRightPadding}`);
      }
    }, 0);
  }

  /**********************************************************************/
  /* GOLDEN LAYOUT */

  public setParentPopup(parentPopup: GoldenLayoutPopout) {
    this.parentPopout = parentPopup;
    this.parentPopout.setShowDock(false);
  }

  public resize($event: any): void {
    //this.adjustContentHeight();
  }

  public resizeDocked(event: any): void {}

  public closeWindow(): void {
    this.parentPopout.close();
  }

  // return false - cancels the popout closing
  public onClose(): boolean {
    return true;
  }

  /**********************************************************************/
  /* ACTIONS */
  public closeWarning(): void {
    this.manualPricingManager.closeWarning(this.activeFixingTabData.id);
  }

  public toggleSort(): void {
    this.ascending = !this.ascending;
    this.manualPricingManager.sortData(this.ascending);
    if (this.ascending) {
      this.sortingTitle = "Ascending";
    } else {
      this.sortingTitle = "Descending";
    }
  }

  public price(): void {
    if (this.activeFixingTabData != null) {
      this.manualPricingManager.price(this.activeFixingTabData.id);
    } else {
      logger.out.error(
        `Manual-Price-Container: Invalid activeTabData %O`,
        this.activeFixingTabData,
      );
    }
  }

  public cancel(): void {
    this.manualPricingManager.closeAll();
  }

  public close() {
    this.manualPricingManager.closeAll();
  }

  public handleTabChange(fixingCode: string) {
    this.setActiveFixingTab(fixingCode);
  }

  public handleCheckboxChange(): void {
    this.applyRowImpactOnHeaderCheckbox();
    this.updatePriceButtonState();
  }

  public handleHeaderCheckboxChange(): void {
    this.applyHeaderImpactOnRowCheckboxes();
    this.updatePriceButtonState();
  }

  public handleBidBlur(id: string): void {
    if (typeof id === "string") {
      let rateDataItem = this.activeFixingTabData.rateData.find((item: any) => item.id === id);
      if (rateDataItem instanceof ManualPricingRateModel && rateDataItem.validBidOffer()) {
        if (rateDataItem.bid.isGreaterThan(rateDataItem.offer)) {
          rateDataItem.offer = new CubiqTS.BigNumber(rateDataItem.bid);
        }
        this.calculateMidRate(id, rateDataItem);
      } else {
        logger.out.error(
          `Manual-Price-Container: handleBidChange(): Rate data not found (id: ${id})`,
        );
      }
    } else {
      logger.out.error(
        `Manual-Price-Container: handleBidChange(): Invalid input parameter (id: string = ${id})`,
      );
    }
  }

  public handleOfferBlur(id: string): void {
    if (typeof id === "string") {
      let rateDataItem = this.activeFixingTabData.rateData.find((item: any) => item.id === id);
      if (rateDataItem instanceof ManualPricingRateModel && rateDataItem.validBidOffer()) {
        if (rateDataItem.offer.isLessThan(rateDataItem.bid)) {
          rateDataItem.offer = new CubiqTS.BigNumber(rateDataItem.bid);
        }
        this.calculateMidRate(id, rateDataItem);
      } else {
        logger.out.error(
          `Manual-Price-Container: handleOfferChange(): Rate data not found (id: ${id})`,
        );
      }
    } else {
      logger.out.error(
        `Manual-Price-Container: handleOfferChange(): Invalid input parameter (id: string = ${id})`,
      );
    }
  }

  public handleSpinnerChange(id: string): void {
    if (typeof id === "string") {
      let rateDataItem = this.activeFixingTabData.rateData.find((item: any) => item.id === id);
      if (rateDataItem instanceof ManualPricingRateModel && rateDataItem.validBidOffer()) {
        if (rateDataItem.offer.isGreaterThanOrEqualTo(rateDataItem.bid)) {
          this.calculateMidRate(id);
        }
      }
    }
  }

  /**********************************************************************/
  /* Mid Rate Calculation */
  private calculateMidRate(id: string, rateDataItem?: ManualPricingRateModel): void {
    if (typeof id === "string" || rateDataItem instanceof ManualPricingRateModel) {
      if (!(rateDataItem instanceof ManualPricingRateModel)) {
        rateDataItem = this.activeFixingTabData.rateData.find((item: any) => item.id === id);
      }

      if (rateDataItem instanceof ManualPricingRateModel && rateDataItem.validBidOffer()) {
        let midValue = new CubiqTS.BigNumber(
          rateDataItem.bid.plus(rateDataItem.offer).dividedBy(2),
        );
        rateDataItem.mid = midValue.toFixed(rateDataItem.midPrecision);
      } else {
        logger.out.error(
          `Manual-Price-Container: calculateMidRate(): Rate data not found (id: ${id})`,
        );
      }
    } else {
      logger.out.error(
        `Manual-Price-Container: calculateMidRate(): Invalid input parameter (id: string = ${id})`,
      );
    }
  }

  /**********************************************************************/
  /* Checkbox */

  private applyRowImpactOnHeaderCheckbox(): void {
    let headerCBState: boolean = true;
    for (let rateDataItem of this.activeFixingTabData.rateData) {
      headerCBState = headerCBState && rateDataItem.checkbox.value;
    }

    if (this.activeFixingTabData.checkboxFixing.value != headerCBState) {
      this.activeFixingTabData.checkboxFixing.value = headerCBState;
    }

    this.applyFirstLastOnRow();
  }

  private applyHeaderImpactOnRowCheckboxes(): void {
    let headerCBState = this.activeFixingTabData.checkboxFixing.value;

    for (let rateDataItem of this.activeFixingTabData.rateData) {
      if (rateDataItem instanceof ManualPricingRateModel && !rateDataItem.disabled) {
        rateDataItem.checkbox.value = headerCBState;
      }
    }

    this.applyFirstLastOnRow();
  }

  private applyFirstLastOnRow(): void {
    let prevSelected: boolean = false;
    let prevRowSelected: ManualPricingRateModel = null;

    for (let rateDataItem of this.activeFixingTabData.rateData) {
      rateDataItem.first = rateDataItem.last = false;

      if (rateDataItem instanceof ManualPricingRateModel && !rateDataItem.disabled) {
        if (rateDataItem.checkbox.value == true && !prevSelected) {
          prevSelected = true;
          rateDataItem.first = true;
          if (prevRowSelected != null) {
            prevRowSelected.last = true;
          }
        }

        if (rateDataItem.checkbox.value == true) {
          prevRowSelected = rateDataItem;
        }
      }
    }

    if (prevRowSelected != null) {
      prevRowSelected.last = true;
    }
  }

  /**********************************************************************/
  /* Price button state */

  private updatePriceButtonState(): void {
    let newStateDisabled: boolean = true;
    for (let rateDataItem of this.activeFixingTabData.rateData) {
      if (
        rateDataItem instanceof ManualPricingRateModel &&
        !rateDataItem.disabled &&
        rateDataItem.checkbox.value
      ) {
        newStateDisabled = false;
      }
    }
    this.disablePrice = newStateDisabled;
  }
}
